import { useState, useRef, useCallback, useEffect } from 'react';

interface Item {
  id: string;
  label: string;
  service: string;
  before: string;
  after: string;
}

const items: Item[] = [
  {
    id: 'ba1',
    label: 'Koltuk Yıkama',
    service: 'Koltuk Yıkama',
    before:
      'https://readdy.ai/api/search-image?query=dirty%20stained%20grey%20fabric%20sofa%20with%20visible%20dirt%20stains%20yellow%20spots%20worn%20upholstery%20before%20cleaning%20indoor%20living%20room%20realistic%20photo&width=700&height=500&seq=ba01&orientation=landscape',
    after:
      'https://readdy.ai/api/search-image?query=clean%20fresh%20spotless%20grey%20fabric%20sofa%20after%20professional%20steam%20cleaning%20bright%20white%20clean%20living%20room%20like%20new%20realistic%20photo&width=700&height=500&seq=ba02&orientation=landscape',
  },
  {
    id: 'ba2',
    label: 'Yatak Yıkama',
    service: 'Yatak Yıkama',
    before:
      'https://readdy.ai/api/search-image?query=stained%20mattress%20with%20yellow%20sweat%20stains%20dirty%20bed%20surface%20before%20deep%20cleaning%20bedroom%20realistic%20photo&width=700&height=500&seq=ba03&orientation=landscape',
    after:
      'https://readdy.ai/api/search-image?query=clean%20white%20mattress%20after%20professional%20deep%20cleaning%20fresh%20bright%20spotless%20bed%20surface%20bedroom%20realistic%20photo&width=700&height=500&seq=ba04&orientation=landscape',
  },
  {
    id: 'ba3',
    label: 'Petek Temizliği',
    service: 'Petek Temizliği',
    before:
      'https://readdy.ai/api/search-image?query=dirty%20rusty%20radiator%20heater%20with%20brown%20sludge%20dust%20buildup%20before%20cleaning%20apartment%20wall%20realistic%20photo&width=700&height=500&seq=ba05&orientation=landscape',
    after:
      'https://readdy.ai/api/search-image?query=clean%20shiny%20white%20radiator%20heater%20after%20professional%20flushing%20service%20apartment%20wall%20bright%20room%20realistic%20photo&width=700&height=500&seq=ba06&orientation=landscape',
  },
];

function Slider({ item }: { item: Item }) {
  const [pos, setPos] = useState(50);
  const [dragging, setDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const updatePos = useCallback((clientX: number) => {
    const el = containerRef.current;
    if (!el) return;
    const { left, width } = el.getBoundingClientRect();
    const pct = Math.min(100, Math.max(0, ((clientX - left) / width) * 100));
    setPos(pct);
  }, []);

  const onMouseDown = () => setDragging(true);
  const onMouseUp = useCallback(() => setDragging(false), []);
  const onMouseMove = useCallback(
    (e: MouseEvent) => { if (dragging) updatePos(e.clientX); },
    [dragging, updatePos]
  );

  const onTouchMove = useCallback(
    (e: TouchEvent) => { updatePos(e.touches[0].clientX); },
    [updatePos]
  );

  useEffect(() => {
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);
    return () => {
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [onMouseUp, onMouseMove]);

  return (
    <div className="flex flex-col gap-0 rounded-2xl overflow-hidden border border-gray-100 group">
      {/* Slider */}
      <div
        ref={containerRef}
        className="relative w-full h-56 md:h-72 cursor-ew-resize select-none overflow-hidden"
        onMouseDown={onMouseDown}
        onTouchMove={(e) => onTouchMove(e.nativeEvent)}
        onTouchStart={(e) => updatePos(e.touches[0].clientX)}
      >
        {/* After (full) */}
        <img
          src={item.after}
          alt={`${item.label} sonra`}
          className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
          draggable={false}
        />

        {/* Before (clipped) */}
        <div
          className="absolute inset-0 overflow-hidden"
          style={{ width: `${pos}%` }}
        >
          <img
            src={item.before}
            alt={`${item.label} önce`}
            className="absolute inset-0 w-full h-full object-cover object-top pointer-events-none"
            style={{ width: containerRef.current ? `${containerRef.current.clientWidth}px` : '100%', maxWidth: 'none' }}
            draggable={false}
          />
        </div>

        {/* Divider line */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-white shadow-lg z-20"
          style={{ left: `${pos}%` }}
        />

        {/* Handle */}
        <div
          className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 z-30 w-10 h-10 flex items-center justify-center bg-white rounded-full shadow-xl border-2 border-emerald-400"
          style={{ left: `${pos}%` }}
        >
          <span className="w-5 h-5 flex items-center justify-center">
            <i className="ri-drag-move-2-line text-emerald-600 text-sm" />
          </span>
        </div>

        {/* Labels */}
        <span className="absolute top-3 left-3 z-20 bg-red-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
          Önce
        </span>
        <span className="absolute top-3 right-3 z-20 bg-emerald-500 text-white text-xs font-bold px-2.5 py-1 rounded-full">
          Sonra
        </span>

        {/* Hint */}
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 bg-black/40 backdrop-blur-sm text-white text-xs px-3 py-1 rounded-full opacity-70 group-hover:opacity-0 transition-opacity pointer-events-none whitespace-nowrap">
          <i className="ri-drag-move-2-line mr-1" />Kaydırarak karşılaştır
        </div>
      </div>

      {/* Caption */}
      <div className="bg-white px-5 py-3 flex items-center justify-between">
        <span className="font-semibold text-[#1a1a1a] text-sm">{item.label}</span>
        <span className="text-xs bg-emerald-50 text-emerald-700 font-semibold px-3 py-1 rounded-full">{item.service}</span>
      </div>
    </div>
  );
}

export default function BeforeAfterSection() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) el.classList.add('opacity-100', 'translate-y-0'); },
      { threshold: 0.05 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section id="before-after" className="w-full bg-[#f8faf9] py-16 md:py-24">
      <div className="max-w-6xl mx-auto px-4">
        <div
          ref={ref}
          className="opacity-0 translate-y-8 transition-all duration-700"
        >
          {/* Header */}
          <div className="text-center mb-12">
            <span className="inline-block text-emerald-600 font-semibold text-sm uppercase tracking-widest mb-3">
              Sonuçlar
            </span>
            <h2
              className="text-3xl md:text-5xl font-extrabold text-[#1a1a1a] leading-tight"
              style={{ fontFamily: 'Poppins, sans-serif' }}
            >
              Önce &amp; Sonra
            </h2>
            <p className="text-gray-500 mt-3 text-base md:text-lg max-w-xl mx-auto">
              Görseli kaydırarak farkı kendiniz görün — filtre yok, düzenleme yok.
            </p>
          </div>

          {/* Sliders Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {items.map((item) => (
              <Slider key={item.id} item={item} />
            ))}
          </div>

          {/* Bottom note */}
          <p className="text-center text-gray-400 text-xs mt-8 flex items-center justify-center gap-1.5">
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-information-line" />
            </span>
            Tüm görseller gerçek müşteri uygulamalarından alınmıştır.
          </p>
        </div>
      </div>
    </section>
  );
}
